import React, { useState, useEffect, useContext } from 'react';
import { 
    Box, Grid, Paper, Typography, Button, TextField, MenuItem, 
    Divider, Accordion, AccordionSummary, AccordionDetails, 
    IconButton, Switch, FormControlLabel
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faChevronDown, faTicket, faUser, faMoneyBillWave, 
    faTrash, faPrint, faCheckCircle, faMinusCircle, faPlusCircle, faPersonCircleExclamation
} from '@fortawesome/free-solid-svg-icons';
import { SeatsioSeatingChart } from '@seatsio/seatsio-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import ReactInputVerificationCode from "react-input-verification-code";

import { GetEventByID } from "../../../../controllers/customer/EventosController";
import { GetAllBankAccounts } from "../../../../controllers/company/AccountNumbersController";
import { isNumeric } from "../../../../controllers/AuxController";
import UserContext from '../../../../navigation/context/userContext';
import QUDProgress from '../../../../components/progress';
import PrintWithQZTray from '../../../../components/QZPrint';
import { printWithRawBT, printWithWebBluetooth } from '../../../../controllers/PrinterController';
import { getBoxOfficeEventDetails } from '../../../../controllers/company/events/MerchantEventController';
import { IssueTicketsAtBoxOffice } from '../../../../controllers/company/events/MerchantTicketController';


export default function EventBoxOfficePOS({ event, country }) {
    const { userData } = useContext(UserContext);

    const merchantUuid = localStorage.getItem('work_mode_merchant_id');

    // ==========================================
    // VARIABLES FINANCIERAS DINÁMICAS
    // ==========================================
    const currencySymbol = event?.financial?.base_currency || event?.moneda || 'USD';
    const dynamicExchangeRate = event?.financial?.exchange_rate || 1;

    // --- ESTADOS DE LA UI ---
    const [expandedPanel, setExpandedPanel] = useState('panel-tickets');
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    
    // --- ESTADOS DEL CARRITO Y EVENTO ---
    const [sections, setSections] = useState([]); 
    const [categories, setCategories] = useState([]); 
    const [selectedSeats, setSelectedSeats] = useState([]);
    const [holdToken, setHoldToken] = useState('');
    const [ticketsIssued, setTicketsIssued] = useState([]);
    
    // --- ESTADOS DEL CLIENTE ---
    const [customer, setCustomer] = useState({
        nombre: '', apellido: '', documento_tipo: 'V', documento: '', telefono: '', correo: ''
    });

    // --- ESTADOS DE PAGO ---
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [bankAccountsAux, setBankAccountsAux] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [bankAccountSelected, setBankAccountSelected] = useState(null);
    const [amount, setAmount] = useState('');
    const [reference, setReference] = useState('');
    const [payments, setPayments] = useState([]);
    
    const [isComplimentary, setIsComplimentary] = useState(false);
    const [complimentaryCode, setComplimentaryCode] = useState('');
    const [complimentaryCodeError, setComplimentaryCodeError] = useState(false);
    
    // --- TOTALES ---
    const [totals, setTotals] = useState({ subtotal: 0, commission: 0, feeSeats: 0, total: 0 });
    const totalPaid = payments.reduce((acc, curr) => acc + curr.amount, 0);
    const balanceDue = Math.max(0, totals.total - totalPaid);

    // ==========================================
    // INICIALIZACIÓN Y FETCH DE DATOS
    // ==========================================
    useEffect(() => {
        console.log('ESTOY MOSTRANDO UN EVENTO Y ES ===>>> ',event)
        // FIX 1: Cargamos las secciones directamente del evento si ya vienen listas (Evita el panel vacío)
        if (event && event.secciones && event.secciones.length > 0) {
            handleSettingTickets(event.secciones);
        } else if (event && event.id_evento) {
            handleGetSections(); // Fallback por si acaso
        }
        handleGetPaymentsMethods();
    }, [event]); 

    const handleGetSections = async () => {
        if (!event || !event.id_evento) return; 

        setIsLoading(true);
        let response = await getBoxOfficeEventDetails(event.id_evento);
        
        if (response.success && response.data?.sections) {
            handleSettingTickets(response.data.sections);
        }
        
        setIsLoading(false);
    };

    const handleSettingTickets = (_tickets) => {
        let _sections = [];
        let _categories = [];
        if (_tickets && Array.isArray(_tickets)) {
            _tickets.forEach(item => {
                _sections.push({
                    evento_seccion_id: item.evento_seccion_id,
                    uuid: item.uuid,
                    nombre: item.nombre,
                    descripcion: item.descripcion,
                    precio: parseFloat(item.precio || 0),
                    tipo_asiento: item.tipo_asiento,
                    cantidad: 0,
                    seleccion_max: item.seleccion_max || 10,
                    lista_asientos: [],
                    capacidad: item.capacidad,
                    vendidos: item.vendidos,
                    disponibles: item.capacidad - item.vendidos
                });
                _categories.push({ category: item.nombre, price: item.precio });
            });
            setSections(_sections);
            setCategories(_categories);
        }
    };

    const handleGetPaymentsMethods = async () => {
        let response = await GetAllBankAccounts(merchantUuid);
        
        if (response.success && response.data && response.data.accounts) {
            const accountsData = response.data.accounts;
            setBankAccounts(accountsData);

            const uniqueMethods = [...new Set(accountsData.map(item => item.payment_method))];
            const mappedMethods = uniqueMethods.map((method, index) => ({
                id: index,
                nombre: method
            }));

            setPaymentMethods(mappedMethods);
            if (mappedMethods.length > 0) {
                setPaymentMethod(mappedMethods[0]);
            }
        }
    };

    // ==========================================
    // LÓGICA DE BOLETOS Y ASIENTOS
    // ==========================================
    const handleShoppingCart = (_uuid, _operator) => {
        setSections(prev => prev.map(item => {
            if (item.uuid === _uuid) {
                let _cantidad = item.cantidad;
                if (_operator === '+' && _cantidad < item.disponibles && _cantidad < item.seleccion_max) _cantidad++;
                else if (_operator === '-' && _cantidad > 0) _cantidad--;

                let _lista = [];
                for(let i=0; i<_cantidad; i++) _lista.push({ fila: item.nombre, asiento: null });
                return { ...item, cantidad: _cantidad, lista_asientos: _lista };
            }
            return item;
        }));
    };

    const handleSelectingSeat = (selectedObject, _operator) => {
        setSections(prev => {
            const updated = [...prev];
            const index = updated.findIndex(item => item.nombre === selectedObject.category.label);
            if (index !== -1) {
                const seats = updated[index].lista_asientos;
                if (_operator === '+') {
                    if (selectedObject.seats) {
                        selectedObject.seats.forEach(item => {
                            const [label, fil, col] = item.label.split('-');
                            if (!seats.find(s => s.fila === fil && s.asiento === col)) {
                                updated[index].lista_asientos.push({ fila: fil, asiento: col });
                            }
                        });
                    } else {
                        if (!seats.find(s => s.fila === selectedObject.labels.parent && s.asiento === selectedObject.labels.own)) {
                            updated[index].lista_asientos.push({ fila: selectedObject.labels.parent, asiento: selectedObject.labels.own });
                        }
                    }
                } else {
                    if (selectedObject.seats) {
                        selectedObject.seats.forEach(item => {
                            const [, fil, col] = item.label.split('-');
                            updated[index].lista_asientos = updated[index].lista_asientos.filter(s => !(s.fila === fil && s.asiento === col));
                        });
                    } else {
                        updated[index].lista_asientos = updated[index].lista_asientos.filter(s => !(s.fila === selectedObject.labels.parent && s.asiento === selectedObject.labels.own));
                    }
                }
                updated[index].cantidad = updated[index].lista_asientos.length;
            }
            return updated;
        });
    };

    useEffect(() => {
        let calcSubtotal = 0;
        let calcFeeSeats = 0;
        let calcCommission = 0;

        sections.forEach(sec => {
            if (sec.cantidad > 0) {
                let precioBoleto = isComplimentary ? 0 : sec.precio;
                calcSubtotal += (sec.cantidad * precioBoleto);

                if (!isComplimentary) {
                    let comisionPorcentaje = precioBoleto * (event?.comision / 100 || 0);
                    let comisionUnitaria = Math.max(comisionPorcentaje, event?.comision_minima || 0);
                    calcCommission += (comisionUnitaria * sec.cantidad);

                    if (parseFloat(event?.fee_asiento) > 0 && (sec.tipo_asiento === 1 || sec.tipo_asiento === 3)) {
                        calcFeeSeats += (sec.cantidad * parseFloat(event.fee_asiento));
                    }
                }
            }
        });

        let calcTotal = calcSubtotal + calcCommission + calcFeeSeats;

        setTotals({ 
            subtotal: calcSubtotal, 
            commission: calcCommission, 
            feeSeats: calcFeeSeats,
            total: calcSubtotal + calcCommission + calcFeeSeats 
        });
    }, [sections, isComplimentary, event]);

    // ==========================================
    // LÓGICA DE CLIENTE Y PAGOS
    // ==========================================
    const handleCustomerChange = (e) => {
        let value = e.target.value;
        if (e.target.name === "documento") value = value.replace(/[^\d]/g, ''); 
        setCustomer({ ...customer, [e.target.name]: value });
    };

    const handleGenericPerson = () => {
        setCustomer({ nombre: 'Consumidor', apellido: 'Final', documento_tipo: 'V', documento: '00000000', telefono: '580000000000', correo: 'info@qud.life' });
        setExpandedPanel('panel-payments');
    };

    useEffect(() => {
        if (paymentMethod && bankAccounts) {
            let _aux = bankAccounts.filter(item => item.payment_method === paymentMethod.nombre);
            setBankAccountsAux(_aux);
            if(_aux.length > 0) setBankAccountSelected(_aux[0]);
        }
    }, [paymentMethod, bankAccounts]);

    const handleRecordPayment = () => {
        if (!paymentMethod || !amount || !reference) return;
        setPayments([...payments, {
            uuid: bankAccountSelected.uuid,
            paymentMethod: bankAccountSelected.payment_method,
            accountName: bankAccountSelected.description || bankAccountSelected.banco,
            amount: parseFloat(amount),
            reference: reference,
            id_numero_cuenta: bankAccountSelected.id
        }]);
        setAmount(''); setReference('');
    };

    // ==========================================
    // EMISIÓN Y API
    // ==========================================
    const handleIssueTickets = async () => {
        setIsLoading(true);

        if (isComplimentary && !isNumeric(complimentaryCode)) {
            setComplimentaryCodeError(true);
            setIsLoading(false);
            return;
        }

        // FIX 2: Blindamos el payload para garantizar que PHP no reciba arrays extraños que causen "Undefined array key fila"
        const detallesFormateados = sections.filter(sec => sec.cantidad > 0).map(sec => {
            let listaLimpia = [];
            if (sec.lista_asientos && sec.lista_asientos.length > 0) {
                listaLimpia = sec.lista_asientos.map(asiento => ({
                    fila: asiento.fila ? String(asiento.fila) : null,
                    asiento: asiento.asiento ? String(asiento.asiento) : null
                }));
            } else {
                for(let i = 0; i < sec.cantidad; i++) {
                    listaLimpia.push({ fila: null, asiento: null });
                }
            }

            return {
                evento_seccion_id: sec.evento_seccion_id,
                uuid: sec.uuid,
                cantidad: sec.cantidad,
                precio: sec.precio,
                lista_asientos: listaLimpia,
                descripcion: sec.descripcion || ''
            };
        });

        const payload = {
            id_evento: event.id_evento,
            cliente: { ...customer },
            monto: totals.subtotal,
            comision: totals.commission,
            detalles: detallesFormateados,
            pagos: payments,
            isComplementary: isComplimentary,
            complimentaryCode: complimentaryCode,
            holdToken: holdToken,
            eventKey: event.event_key,
            selectedTickets: selectedSeats,
        };
        
        let response = await IssueTicketsAtBoxOffice(payload);

        if (response.success) {
            setTicketsIssued(response.data.tickets);
            setIsSuccessModalOpen(true);
        } else {
            alert("Error: " + response.message);
        }
        setIsLoading(false);
    };

    const isReadyToEmit = sections.some(s => s.cantidad > 0) && customer.documento !== '' && (balanceDue === 0 || isComplimentary);

    const handleReset = () => {
        // 1. Ocultamos el modal de éxito y volvemos al primer panel
        setIsSuccessModalOpen(false);
        setExpandedPanel('panel-tickets');

        // 2. Limpiamos el carrito (volvemos las cantidades a 0 y vaciamos los asientos)
        setSections(prev => prev.map(sec => ({
            ...sec,
            cantidad: 0,
            lista_asientos: []
        })));
        setSelectedSeats([]);
        setHoldToken('');
        setTicketsIssued([]);

        // 3. Limpiamos el formulario del cliente
        setCustomer({
            nombre: '', apellido: '', documento_tipo: 'V', documento: '', telefono: '', correo: ''
        });

        // 4. Limpiamos los pagos y cortesías
        setAmount('');
        setReference('');
        setPayments([]);
        setIsComplimentary(false);
        setComplimentaryCode('');
        setComplimentaryCodeError(false);
    };

    // ==========================================
    // RENDERIZADO
    // ==========================================
    if (isSuccessModalOpen) {
        return (
            <Paper elevation={0} sx={{ p: 5, textAlign: 'center', borderRadius: 4, border: '1px solid #e0e0e0' }}>
                <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 80, color: '#2e7d32', marginBottom: 20 }} />
                <Typography variant="h4" fontWeight="bold" gutterBottom>¡Operación Exitosa!</Typography>
                <Typography variant="body1" color="text.secondary" mb={4}>Los boletos han sido emitidos correctamente.</Typography>
                
                <Grid container spacing={2} justifyContent="center" maxWidth={600} margin="0 auto">
                    <Grid item xs={12} sm={4}>
                        <Button variant="contained" color="primary" fullWidth onClick={() => printWithRawBT(userData.entity.last_name, event.nombre, ticketsIssued)}>
                            Imprimir (Android)
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button variant="contained" color="secondary" fullWidth onClick={() => printWithWebBluetooth(userData.entity.last_name, event.nombre, ticketsIssued)}>
                            Imprimir (Web BT)
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button variant="outlined" fullWidth onClick={handleReset}>Nuevo Cliente</Button>
                    </Grid>
                </Grid>
                <PrintWithQZTray merchant={userData.user.apellido} event={event.nombre} tickets={ticketsIssued} />
            </Paper>
        );
    }

    return (
        <Box>
            <QUDProgress open={isLoading} />
            <Grid container spacing={3} alignItems="flex-start">
                
                {/* --- COLUMNA IZQUIERDA (ACORDEONES) --- */}
                <Grid item xs={12} md={8}>
                    
                    {/* 1. BOLETOS */}
                    <Accordion expanded={expandedPanel === 'panel-tickets'} onChange={(e, isExp) => setExpandedPanel(isExp ? 'panel-tickets' : false)} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '12px !important', mb: 2, '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <FontAwesomeIcon icon={faTicket} style={{ color: '#1976d2', fontSize: 20 }} />
                                <Typography variant="h6" fontWeight="bold">1. Selección de Boletos</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            {event?.event_key ? (
                                <Box sx={{ width: '100%', height: 500 }}>
                                    <SeatsioSeatingChart
                                        key={event.event_key}
                                        workspaceKey="f0117c1e-e399-4ff5-93c2-ee4219d608f5"
                                        event={event.event_key}
                                        region="sa"
                                        pricing={categories}
                                        priceFormatter={price => `${price} ${currencySymbol}`}
                                        onObjectSelected={(selectedObject) => {
                                            handleSelectingSeat(selectedObject, '+');
                                            setSelectedSeats(selectedObject.chart.selectedSeats);
                                            setHoldToken(selectedObject.chart.holdToken);
                                        }}
                                        onObjectDeselected={(selectableObject) => handleSelectingSeat(selectableObject, '-')}
                                        session="start"
                                    />
                                </Box>
                            ) : (
                                <Box>
                                    {sections.map((item) => (
                                        <Box key={item.uuid} display="flex" justifyContent="space-between" alignItems="center" p={2} borderBottom="1px solid #eee">
                                            <Box>
                                                <Typography fontWeight="bold">{item.nombre}</Typography>
                                                <Typography variant="body2" color="text.secondary">{parseFloat(item.precio).toFixed(2)} {currencySymbol} c/u (Disp: {item.disponibles})</Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center" gap={2}>
                                                <IconButton color="primary" onClick={() => handleShoppingCart(item.uuid, '-')}><FontAwesomeIcon icon={faMinusCircle} /></IconButton>
                                                <Typography fontWeight="bold" fontSize={18}>{item.cantidad}</Typography>
                                                <IconButton color="primary" onClick={() => handleShoppingCart(item.uuid, '+')}><FontAwesomeIcon icon={faPlusCircle} /></IconButton>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            <Box mt={3} textAlign="right">
                                <Button variant="contained" onClick={() => setExpandedPanel('panel-customer')}>Siguiente: Cliente</Button>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* 2. CLIENTE */}
                    <Accordion expanded={expandedPanel === 'panel-customer'} onChange={(e, isExp) => setExpandedPanel(isExp ? 'panel-customer' : false)} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '12px !important', mb: 2, '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <FontAwesomeIcon icon={faUser} style={{ color: '#1976d2', fontSize: 20 }} />
                                <Typography variant="h6" fontWeight="bold">2. Datos del Cliente</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box mb={2} display="flex" justifyContent="flex-end">
                                <Button variant="outlined" color="link" onClick={handleGenericPerson} startIcon={<FontAwesomeIcon icon={faPersonCircleExclamation}/>}>
                                    Consumidor Final
                                </Button>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={4} sm={3}>
                                    <TextField select fullWidth label="Tipo" name="documento_tipo" value={customer.documento_tipo} onChange={handleCustomerChange}>
                                        <MenuItem value="V">V</MenuItem><MenuItem value="E">E</MenuItem><MenuItem value="J">J</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={8} sm={9}>
                                    <TextField fullWidth label="Cédula / RIF" name="documento" required value={customer.documento} onChange={handleCustomerChange} />
                                </Grid>
                                <Grid item xs={12} sm={6}><TextField fullWidth label="Nombre" name="nombre" required value={customer.nombre} onChange={handleCustomerChange} /></Grid>
                                <Grid item xs={12} sm={6}><TextField fullWidth label="Apellido" name="apellido" required value={customer.apellido} onChange={handleCustomerChange} /></Grid>
                                <Grid item xs={12} sm={6}><TextField fullWidth label="Correo Electrónico" name="correo" required value={customer.correo} onChange={handleCustomerChange} /></Grid>
                                <Grid item xs={12} sm={6}>
                                    <PhoneInput country={'ve'} specialLabel="Teléfono" inputStyle={{ width: '100%', height: '56px' }} value={customer.telefono} onChange={(val) => setCustomer({...customer, telefono: val})} />
                                </Grid>
                            </Grid>
                            <Box mt={3} textAlign="right">
                                <Button variant="contained" onClick={() => setExpandedPanel('panel-payments')}>Siguiente: Pago</Button>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* 3. PAGOS */}
                    <Accordion expanded={expandedPanel === 'panel-payments'} onChange={(e, isExp) => setExpandedPanel(isExp ? 'panel-payments' : false)} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '12px !important', '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <FontAwesomeIcon icon={faMoneyBillWave} style={{ color: '#1976d2', fontSize: 20 }} />
                                <Typography variant="h6" fontWeight="bold">3. Método de Pago</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <FormControlLabel control={<Switch checked={isComplimentary} onChange={(e) => setIsComplimentary(e.target.checked)} color="success" />} label={<Typography fontWeight="bold">Marcar como Cortesía</Typography>} sx={{ mb: 3 }} />

                            {isComplimentary ? (
                                <Box textAlign="center" p={3} bgcolor="#f7fbff" borderRadius={2} border="1px dashed #1976d2">
                                    <Typography gutterBottom>Ingrese el código de cortesía:</Typography>
                                    <Box display="flex" justifyContent="center" mb={1}>
                                        <ReactInputVerificationCode type="password" length={6} onChange={(e) => setComplimentaryCode(e)} />
                                    </Box>
                                    {complimentaryCodeError && <Typography color="error" variant="caption">Código inválido</Typography>}
                                </Box>
                            ) : (
                                <Box p={3} >
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField select fullWidth label="Forma de Pago" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                                                {paymentMethods.map(item => <MenuItem key={item.id} value={item}>{item.nombre}</MenuItem>)}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container spacing={3}>
                                                {bankAccountsAux.map(item => (
                                                    <Grid item xs={6} key={item.id} onClick={() => setBankAccountSelected(item)}>
                                                        <Paper sx={{ 
                                                            p: 1.5, 
                                                            cursor: 'pointer', 
                                                            border: '2px solid', 
                                                            borderColor: bankAccountSelected?.id === item.id ? 'link.main' : '#e0e0e0', 
                                                            bgcolor: bankAccountSelected?.id === item.id ? '#f7fbff' : '#fff' 
                                                        }}>
                                                            <Typography variant="body2" fontWeight="bold">
                                                                {item.banco || item.bank?.name || 'Banco'}
                                                            </Typography>
                                                            <Typography variant="caption" display="block">
                                                                {item.numero_cuenta || item.account_number}
                                                            </Typography>
                                                        </Paper>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={6}><TextField fullWidth label={`Monto (${currencySymbol})`} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} /></Grid>
                                        <Grid item xs={6}><TextField fullWidth label="Referencia" value={reference} onChange={(e) => setReference(e.target.value)} /></Grid>
                                        <Grid item xs={12}>
                                            <Button variant="contained" fullWidth onClick={handleRecordPayment} disabled={!amount || !reference || !bankAccountSelected}>
                                                Añadir Pago
                                            </Button>
                                        </Grid>
                                    </Grid>

                                    {payments.length > 0 && (
                                        <Box mt={3}>
                                            <Typography variant="subtitle2" fontWeight="bold" mb={1}>Pagos Registrados:</Typography>
                                            {payments.map((p, idx) => (
                                                <Box key={idx} display="flex" justifyContent="space-between" alignItems="center" bgcolor="#fff" p={1.5} mb={1} borderRadius={1} border="1px solid #ddd">
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold">{p.paymentMethod}</Typography>
                                                        <Typography variant="caption" color="text.secondary">Ref: {p.reference} • {p.accountName}</Typography>
                                                    </Box>
                                                    <Typography fontWeight="bold" color="success.main">+ {p.amount.toFixed(2)} {currencySymbol}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                {/* --- COLUMNA DERECHA (STICKY CART) --- */}
                <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 4, position: 'sticky', top: 20 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom align="center">Resumen de Orden</Typography>
                        
                        {customer.documento && (
                            <Box bgcolor="#e3f2fd" p={1.5} borderRadius={2} mb={2} textAlign="center">
                                <Typography variant="caption" color="primary" fontWeight="bold" textTransform="uppercase">Cliente</Typography>
                                <Typography variant="subtitle2" fontWeight="bold" noWrap>{customer.nombre} {customer.apellido}</Typography>
                                <Typography variant="caption">{customer.documento_tipo}-{customer.documento}</Typography>
                            </Box>
                        )}

                        <Divider sx={{ my: 2, borderStyle: 'dashed' }} />

                        <Box minHeight={80}>
                            {sections.filter(s => s.cantidad > 0).length === 0 ? (
                                <Typography variant="body2" color="text.secondary" align="center" mt={2}>No hay boletos seleccionados.</Typography>
                            ) : (
                                sections.map((sec, idx) => sec.cantidad > 0 && (
                                    <Box key={idx} display="flex" justifyContent="space-between" mb={1}>
                                        <Typography variant="body2">{sec.cantidad}x {sec.nombre}</Typography>
                                        <Typography variant="body2" fontWeight="bold">{(sec.cantidad * sec.precio).toFixed(2)} {currencySymbol}</Typography>
                                    </Box>
                                ))
                            )}
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                            <Typography variant="body2">{totals.subtotal.toFixed(2)} {currencySymbol}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" color="text.secondary">Service Fee</Typography>
                            <Typography variant="body2">{totals.commission.toFixed(2)} {currencySymbol}</Typography>
                        </Box>

                        {totals.feeSeats > 0 && (
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="body2" color="text.secondary">Recargo Asientos</Typography>
                                <Typography variant="body2">{totals.feeSeats.toFixed(2)} {currencySymbol}</Typography>
                            </Box>
                        )}

                        <Box display="flex" justifyContent="space-between" mt={2} p={1.5} bgcolor="#f5f5f5" borderRadius={2}>
                            <Typography variant="subtitle1" fontWeight="bold">TOTAL</Typography>
                            <Typography variant="subtitle1" fontWeight="bold" color="primary.main">{totals.total.toFixed(2)} {currencySymbol}</Typography>
                        </Box>

                        {!isComplimentary && totals.total > 0 && (
                            <Box mt={2}>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" color="success.main" fontWeight="bold">Pagado: {totalPaid.toFixed(2)} {currencySymbol}</Typography>
                                    <Typography variant="body2" color="error.main" fontWeight="bold">Restante: {balanceDue.toFixed(2)} {currencySymbol}</Typography>
                                </Box>
                                {balanceDue > 0 && <Typography variant="caption" color="text.secondary" display="block" align="right" mt={0.5}>VES {(balanceDue * dynamicExchangeRate).toFixed(2)}</Typography>}
                            </Box>
                        )}

                        <LoadingButton 
                            loading={isLoading} variant="contained" size="large" fullWidth disableElevation
                            disabled={!isReadyToEmit} onClick={handleIssueTickets}
                            sx={{ mt: 3, py: 1.5, borderRadius: 2, fontWeight: 'bold' }}
                        >
                            {isComplimentary ? 'EMITIR CORTESÍA' : 'EMITIR BOLETOS'}
                        </LoadingButton>
                    </Paper>
                </Grid>

            </Grid>
        </Box>
    );
}